import { ClassValue } from 'clsx';
import { ChangeEvent, ReactNode } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

import { cn } from '@/utils/styles';

import { colors, sizes } from './constants';
import { CheckboxColor, CheckboxSize } from './types';

export type CheckboxProps = {
  readonly id?: string;
  readonly value?: string;
  readonly size?: CheckboxSize;
  readonly color?: CheckboxColor;
  readonly autoComplete?: string;
  readonly registration?: Partial<UseFormRegisterReturn>;
  readonly children?: ReactNode;
  readonly classes?: {
    main?: ClassValue;
    input?: ClassValue;
    label?: ClassValue;
  };
  readonly checked?: boolean;
  readonly disabled?: boolean;
  readonly onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};

export function Checkbox({
  id,
  value,
  size = 'md',
  color = 'primary',
  autoComplete = 'off',
  registration,
  children,
  classes,
  checked,
  disabled,
  onChange,
}: CheckboxProps) {
  const isDisabled = disabled ?? registration?.disabled ?? false;

  return (
    <div className={cn('flex items-center justify-start gap-x-2', classes?.main)}>
      <input
        type="checkbox"
        id={id ?? value}
        autoComplete={autoComplete}
        className={cn(
          'cursor-pointer transition disabled:cursor-not-allowed',
          sizes[size],
          'appearance-none rounded-sm border shadow-none outline-none',
          'focus:outline-none',
          'focus:ring-2 focus:ring-offset-0',
          colors[color],
          classes?.input
        )}
        value={value}
        checked={checked}
        disabled={isDisabled}
        {...registration}
        onChange={(e) => {
          registration?.onChange?.(e);
          onChange?.(e);
        }}
      />

      {children ? (
        <label
          htmlFor={id ?? value}
          className={cn(
            'block text-base leading-4 font-normal',
            isDisabled ? 'text-neutral-disabled cursor-not-allowed' : 'text-base-primary cursor-pointer',
            classes?.label
          )}
        >
          {children}
        </label>
      ) : null}
    </div>
  );
}
